/**
 * FieldGroup Component
 *
 * A theme-aware layout component for organizing form fields in responsive grids.
 * Provides structured grouping with consistent spacing and responsive behavior.
 *
 * IMPORTANT:
 * - Uses ONLY theme tokens (NO hardcoded values)
 * - Supports responsive grid layouts (1-4 columns)
 * - Can be used standalone or nested within SmartSection
 * - Optimized with React.memo
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  useTheme,
  SxProps,
  Theme,
  Breakpoint
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Help as HelpIcon
} from '@mui/icons-material';

// ============================================================================
// COMPONENT INTERFACES
// ============================================================================

export interface FieldGroupProps {
  /**
   * Content
   */
  children: React.ReactNode;

  /**
   * Layout Configuration
   */
  columns?: number | Partial<Record<Breakpoint, number>>;
  spacing?: number;

  /**
   * Group Information
   */
  label?: string;
  helperText?: string;
  description?: string;

  /**
   * Behavior
   */
  collapsible?: boolean;
  defaultExpanded?: boolean;
  disabled?: boolean;

  /**
   * Visual Variants
   */
  variant?: 'default' | 'outlined' | 'elevated' | 'minimal';

  /**
   * Help System
   */
  helpId?: string;
  onHelpClick?: (helpId: string) => void;

  /**
   * Event Handlers
   */
  onToggle?: (expanded: boolean) => void;

  /**
   * Accessibility
   */
  'aria-label'?: string;
  'aria-describedby'?: string;
  id?: string;

  /**
   * Custom Styling (Theme-Based)
   */
  sx?: SxProps<Theme>;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const FieldGroup = React.memo<FieldGroupProps>(({
  children,
  columns = { xs: 1, sm: 2, md: 3, lg: 4 },
  spacing = 2,
  label,
  helperText,
  description,
  collapsible = false,
  defaultExpanded = true,
  disabled = false,
  variant = 'default',
  helpId,
  onHelpClick,
  onToggle,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  id,
  sx,
}) => {
  const theme = useTheme();

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleToggle = useCallback(() => {
    if (disabled) return;

    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggle?.(newExpanded);
  }, [disabled, isExpanded, onToggle]);

  const handleHelpClick = useCallback((event: React.MouseEvent) => {
    event.stopPropagation();
    if (helpId) {
      onHelpClick?.(helpId);
    }
  }, [helpId, onHelpClick]);

  // ============================================================================
  // GRID CONFIGURATION
  // ============================================================================

  const gridTemplateColumns = useMemo(() => {
    if (typeof columns === 'number') {
      return `repeat(${columns}, 1fr)`;
    }

    // Handle responsive columns object
    const breakpoints = theme.breakpoints.keys;
    const mediaQueries: string[] = [];

    breakpoints.forEach((breakpoint) => {
      if (columns[breakpoint]) {
        const minWidth = theme.breakpoints.values[breakpoint];
        mediaQueries.push(
          `@media (min-width: ${minWidth}px) { grid-template-columns: repeat(${columns[breakpoint]}, 1fr); }`
        );
      }
    });

    // Default to single column for smallest screens
    return 'repeat(1, 1fr)';
  }, [columns, theme.breakpoints]);

  // ============================================================================
  // THEME-AWARE STYLING
  // ============================================================================

  const containerStyles = useMemo((): SxProps<Theme> => {
    const baseStyles: SxProps<Theme> = {
      borderRadius: theme.shape.borderRadius,
      transition: theme.transitions.create(['background-color', 'border-color', 'box-shadow'], {
        duration: theme.transitions.duration.short,
      }),
    };

    switch (variant) {
      case 'outlined':
        return {
          ...baseStyles,
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          '&:hover': disabled ? {} : {
            borderColor: theme.palette.text.primary,
          },
        };

      case 'elevated':
        return {
          ...baseStyles,
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[1],
          '&:hover': disabled ? {} : {
            boxShadow: theme.shadows[2],
          },
        };

      case 'minimal':
        return {
          ...baseStyles,
          backgroundColor: 'transparent',
        };

      default: // 'default'
        return {
          ...baseStyles,
          backgroundColor: theme.palette.background.default,
          border: `1px solid transparent`,
        };
    }
  }, [theme, variant, disabled]);

  const headerStyles = useMemo((): SxProps<Theme> => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderBottom: label && variant !== 'minimal' ? `1px solid ${theme.palette.divider}` : 'none',
    backgroundColor: variant === 'outlined' || variant === 'elevated'
      ? theme.palette.background.default
      : 'transparent',
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    cursor: collapsible ? 'pointer' : 'default',
    userSelect: 'none',
    '&:hover': collapsible && !disabled ? {
      backgroundColor: theme.palette.action.hover,
    } : {},
  }), [theme, label, variant, collapsible, disabled]);

  const contentStyles = useMemo((): SxProps<Theme> => ({
    display: 'grid',
    gridTemplateColumns: typeof columns === 'number'
      ? `repeat(${columns}, 1fr)`
      : {
          xs: `repeat(${(columns as any).xs || 1}, 1fr)`,
          sm: `repeat(${(columns as any).sm || 2}, 1fr)`,
          md: `repeat(${(columns as any).md || 3}, 1fr)`,
          lg: `repeat(${(columns as any).lg || 4}, 1fr)`,
        },
    gap: theme.spacing(spacing),
    padding: theme.spacing(spacing),
    alignItems: 'start',
  }), [columns, spacing, theme]);

  const labelStyles = useMemo((): SxProps<Theme> => ({
    color: disabled ? theme.palette.text.disabled : theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  }), [theme, disabled]);

  const helperTextStyles = useMemo((): SxProps<Theme> => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    margin: theme.spacing(0.5, 2, 1, 2),
  }), [theme]);

  // ============================================================================
  // ACCESSIBILITY ATTRIBUTES
  // ============================================================================

  const accessibilityProps = useMemo(() => ({
    'aria-label': ariaLabel || (label ? `Field group: ${label}` : 'Field group'),
    'aria-describedby': ariaDescribedBy || (helperText ? `${id}-helper` : undefined),
    'aria-expanded': collapsible ? isExpanded : undefined,
    'aria-disabled': disabled,
    role: collapsible ? 'button' : 'group',
    tabIndex: collapsible ? 0 : undefined,
  }), [ariaLabel, label, ariaDescribedBy, helperText, id, collapsible, isExpanded, disabled]);

  // ============================================================================
  // KEYBOARD SUPPORT
  // ============================================================================

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (collapsible && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      handleToggle();
    }
  }, [collapsible, handleToggle]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box
      id={id}
      data-testid="field-group-container"
      sx={{
        ...containerStyles,
        ...(disabled && { opacity: 0.6 }),
        ...(sx as any),
      }}
      {...accessibilityProps}
      onKeyDown={handleKeyDown}
    >
      {/* Header Section */}
      {(label || collapsible) && (
        <Box
          sx={headerStyles}
          onClick={collapsible ? handleToggle : undefined}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {label && (
              <Typography variant="subtitle1" sx={labelStyles}>
                {label}
              </Typography>
            )}

            {helpId && (
              <IconButton
                size="small"
                onClick={handleHelpClick}
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.primary.main,
                  },
                }}
                aria-label={`Help for ${label || 'field group'}`}
              >
                <HelpIcon fontSize="small" />
              </IconButton>
            )}
          </Box>

          {collapsible && (
            <IconButton
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                transition: theme.transitions.create('transform'),
                transform: isExpanded ? 'rotate(0deg)' : 'rotate(-180deg)',
              }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <ExpandMoreIcon />
            </IconButton>
          )}
        </Box>
      )}

      {/* Helper Text */}
      {helperText && (
        <Typography
          variant="caption"
          id={`${id}-helper`}
          sx={helperTextStyles}
        >
          {helperText}
        </Typography>
      )}

      {/* Description */}
      {description && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            margin: theme.spacing(0.5, 2, 1, 2),
          }}
        >
          {description}
        </Typography>
      )}

      {/* Content Area */}
      <Collapse in={!collapsible || isExpanded} timeout="auto" unmountOnExit>
        <Box data-testid="field-group-content" sx={contentStyles}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo optimization
  const relevantProps = [
    'columns', 'spacing', 'label', 'helperText', 'description',
    'collapsible', 'defaultExpanded', 'disabled', 'variant', 'helpId'
  ] as const;

  for (const prop of relevantProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false; // Props changed, re-render needed
    }
  }

  // Check children
  if (React.Children.count(prevProps.children) !== React.Children.count(nextProps.children)) {
    return false;
  }

  // Check sx prop
  if (JSON.stringify(prevProps.sx) !== JSON.stringify(nextProps.sx)) {
    return false;
  }

  return true; // Props haven't changed, skip re-render
});

FieldGroup.displayName = 'FieldGroup';

// ============================================================================
// EXPORTS
// ============================================================================

export { FieldGroup as default };